/**
 * Message variables ({{first_name}}, {{value}}, … one per extra recipient column, and
 * {{kpi:<name>}} per KPI tile), with magic-core's rules (`schemas/distribution/messages.py`):
 * a token is `{{ name }}`, matched trimmed and case-insensitively. Parse, fill in, HTML-escape,
 * find unknown tokens and suggest a fix ("{{firstname}} isn’t a variable. Use {{first_name}} for
 * their first name.").
 */
import { BUILT_IN_TOKENS } from "./distributions.types";
import type { TokenKpiApi, TokenListApi } from "./distributions.types";

const TOKEN_PATTERN = /\{\{([^{}]*)\}\}/g;

export const KPI_TOKEN_PREFIX = "kpi:";

/** Token names in a text, trimmed, in order, repeats kept. */
export const tokensIn = (text: string): string[] => {
  const out: string[] = [];
  String(text ?? "").replace(TOKEN_PATTERN, (match, name: string) => {
    out.push(name.trim());
    return match;
  });
  return out;
};

/** `kpi:<name>`, lower-cased with single spaces, as magic-core `kpi_token`. */
export const kpiToken = (kpiName: string): string =>
  KPI_TOKEN_PREFIX + kpiName.trim().toLowerCase().split(/\s+/).filter(Boolean).join(" ");

/** The variables a message may use: what GET …/tokens answers, or the same built from local data. */
export type TokenCatalog = TokenListApi;

/**
 * The catalog from what the UI already holds: the built-ins, the rows' extra columns and the
 * Story's KPI tile names. Used until (or when) the tokens route can't answer.
 */
export const localTokenCatalog = (extraColumns: readonly string[], kpiNames: readonly string[] = []): TokenCatalog => {
  const builtin: readonly string[] = BUILT_IN_TOKENS;
  // As the route answers: lower-cased, sorted, without a key a built-in already names.
  const columns = Array.from(new Set(extraColumns.map((column) => column.trim().toLowerCase()).filter(Boolean)))
    .filter((column) => !builtin.includes(column))
    .sort();
  const names = Array.from(new Set(kpiNames.map((name) => name.trim()).filter(Boolean)));
  return {
    builtin: [...BUILT_IN_TOKENS],
    columns,
    kpis: names.map((name) => ({ token: kpiToken(name), name })),
  };
};

/** Every known token, lower-case, distinct: built-ins, columns, then KPIs (magic-core `known_tokens`). */
export const catalogTokens = (catalog: TokenCatalog): string[] =>
  Array.from(
    new Set([
      ...catalog.builtin.map((token) => token.toLowerCase()),
      ...catalog.columns.map((column) => column.trim().toLowerCase()),
      ...catalog.kpis.map((kpi) => kpi.token.toLowerCase()),
    ]),
  );

/** Built-ins plus the extra columns (lower snake_case keys) and the KPI tokens. */
export const knownTokens = (extraColumns: readonly string[], kpiNames: readonly string[] = []): string[] =>
  catalogTokens(localTokenCatalog(extraColumns, kpiNames));

/** Distinct tokens in the texts that aren't in the catalog, as written. */
export const unknownTokensIn = (texts: readonly string[], catalog: TokenCatalog): string[] => {
  const known = catalogTokens(catalog);
  return Array.from(new Set(texts.flatMap(tokensIn).filter((name) => !known.includes(name.toLowerCase()))));
};

/** Distinct tokens in the texts that aren't known, as written. */
export const findUnknownTokens = (
  texts: readonly string[],
  extraColumns: readonly string[],
  kpiNames: readonly string[] = [],
): string[] => unknownTokensIn(texts, localTokenCatalog(extraColumns, kpiNames));

/** Extra-column tokens the texts use (lower case, distinct). */
export const usedExtraTokens = (texts: readonly string[], extraColumns: readonly string[]): string[] => {
  const extras = extraColumns.map((column) => column.trim().toLowerCase());
  return Array.from(new Set(texts.flatMap(tokensIn).map((name) => name.toLowerCase()).filter((name) => extras.includes(name))));
};

/** KPI tokens the texts use, with their tile (lower case, distinct, known ones only). */
export const usedKpiTokens = (texts: readonly string[], kpis: readonly TokenKpiApi[]): TokenKpiApi[] => {
  const used = new Set(texts.flatMap(tokensIn).map((name) => name.toLowerCase()));
  return kpis.filter((kpi) => used.has(kpi.token.toLowerCase()));
};

/** Whether the texts use a token (any spacing or case). */
export const usesToken = (texts: readonly string[], token: string): boolean =>
  texts.flatMap(tokensIn).some((name) => name.toLowerCase() === token.toLowerCase());

export const escapeHtml = (text: string): string =>
  String(text ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c);

/** One piece of a message after filling in tokens; `missing` marks a token with no value. */
export interface TokenSegment {
  text: string;
  token: string | null;
  missing: boolean;
}

/**
 * Splits a message into plain text and filled tokens. `values` maps lower-case token names to
 * their value; a token without a non-blank value stays as written and is flagged `missing`.
 */
export const renderTokenSegments = (
  text: string,
  values: Readonly<Record<string, string | null | undefined>>,
): TokenSegment[] =>
  String(text ?? "")
    .split(/(\{\{[^{}]*\}\})/)
    .filter((part) => part !== "")
    .map((part) => {
      const match = /^\{\{([^{}]*)\}\}$/.exec(part);
      if (!match) return { text: part, token: null, missing: false };
      const name = match[1].trim();
      const value = values[name.toLowerCase()];
      return value && value.trim()
        ? { text: value, token: name, missing: false }
        : { text: part, token: name, missing: true };
    });

/** The message as plain text with tokens filled in (missing ones stay as written). */
export const renderTokens = (text: string, values: Readonly<Record<string, string | null | undefined>>): string =>
  renderTokenSegments(text, values)
    .map((segment) => segment.text)
    .join("");

/** The message as HTML-safe text, for an email body preview. */
export const renderTokensHtml = (text: string, values: Readonly<Record<string, string | null | undefined>>): string =>
  escapeHtml(renderTokens(text, values)).replace(/\n/g, "<br>");

export interface TokenSuggestion {
  message: string;
  /** Known token to replace it with, when there's an obvious one. */
  fix: string | null;
}

export interface TokenSuggestionContext {
  /** The split field's label. */
  fieldLabel: string;
  /** Its plural. */
  fieldPlural: string;
  /** Labels of the story's other split fields. */
  otherFieldLabels: readonly string[];
  /** The Story's KPI tiles; their names suggest `{{kpi:<name>}}`. */
  kpis?: readonly TokenKpiApi[];
}

const squash = (text: string): string => text.toLowerCase().replace(/[\s_:-]+/g, "");

/** Words people type for a built-in, and how the built-in reads in the message. */
const WORD_FIXES: [string[], string, string][] = [
  [["firstname", "first", "fname", "givenname", "forename"], "first_name", "their first name"],
  [["lastname", "surname", "familyname", "lname"], "last_name", "their last name"],
  [["fullname", "recipient", "recipientname", "person", "username", "contact"], "name", "their name"],
  [["mail", "emailaddress", "recipientemail", "address"], "email", "their email address"],
  [["today", "rundate", "senddate", "day", "sentdate"], "date", "the send date"],
  [["next", "nextdate", "nextrun", "nextsenddate"], "next_send", "the next send"],
  [["asof", "refreshed", "refreshdate", "datadate", "updated", "lastrefresh", "asofdate"], "data_as_of", "when the data was refreshed"],
  [["title", "report", "storyname", "storytitle", "dashboard"], "story", "the story name"],
  [["me", "from", "owner", "author", "sendername", "myname"], "sender", "your name"],
  [["senderemail", "myemail", "fromemail", "replyto", "owneremail"], "sender_email", "your email address"],
  [["org", "organization", "organisation", "companyname", "tenant", "business"], "company", "your company"],
  [["aisummary", "ai", "insights", "highlights", "summarytext", "aiinsights"], "summary", "the AI summary"],
];

const orList = (items: readonly string[]): string =>
  items.length < 2 ? items.join("") : `${items.slice(0, -1).join(", ")} or ${items[items.length - 1]}`;

/** A KPI whose name matches what was typed: the same letters, or one name inside the other. */
const kpiLike = (typed: string, kpis: readonly TokenKpiApi[]): TokenKpiApi | null => {
  const key = squash(typed);
  if (!key) return null;
  const exact = kpis.find((kpi) => squash(kpi.name) === key);
  if (exact) return exact;
  const close = kpis.filter((kpi) => squash(kpi.name).includes(key) || key.includes(squash(kpi.name)));
  return close.length === 1 ? close[0] : null;
};

/** Why a token is unknown and what to use instead (the prototype's `suggest`). */
export const suggestTokenFix = (token: string, ctx: TokenSuggestionContext): TokenSuggestion => {
  const key = squash(token);
  const written = `{{${token}}}`;
  const kpis = ctx.kpis ?? [];
  if (!key) return { message: "{{}} is empty. Put a variable inside the braces, such as {{first_name}}.", fix: null };
  if (token.trim().toLowerCase().startsWith(KPI_TOKEN_PREFIX)) {
    const typed = token.trim().slice(KPI_TOKEN_PREFIX.length);
    const match = kpiLike(typed, kpis);
    if (match) return { message: `${written} isn’t a KPI in this story. Use {{${match.token}}} for their ${match.name}.`, fix: match.token };
    if (!kpis.length) return { message: `This story has no KPI tiles, so ${written} can’t be filled in.`, fix: null };
    return { message: `${written} isn’t a KPI in this story. Use ${orList(kpis.map((kpi) => `{{${kpi.token}}}`))}.`, fix: null };
  }
  const fieldKey = squash(ctx.fieldLabel);
  if (key === fieldKey || key === squash(ctx.fieldPlural) || key === `${fieldKey}name`) {
    return { message: `${written} isn’t a variable. Use {{value}} for each person’s ${ctx.fieldLabel}.`, fix: "value" };
  }
  if (ctx.otherFieldLabels.some((label) => squash(label) === key)) {
    return {
      message: `${written} isn’t a variable. This distribution splits by ${ctx.fieldLabel}, so use {{value}} for each person’s ${ctx.fieldLabel}.`,
      fix: "value",
    };
  }
  const word = WORD_FIXES.find(([words]) => words.includes(key));
  if (word) return { message: `${written} isn’t a variable. Use {{${word[1]}}} for ${word[2]}.`, fix: word[1] };
  const kpi = kpiLike(token, kpis);
  if (kpi && squash(kpi.name) === key) return { message: `${written} isn’t a variable. Use {{${kpi.token}}} for their ${kpi.name}.`, fix: kpi.token };
  return {
    message: `${written} isn’t a variable. Pick one from Insert variable, or add a ${token.trim()} column to your recipient file.`,
    fix: null,
  };
};

/** Replaces every `{{from}}` (any spacing or case) with `{{to}}`: the one-click fix. */
export const replaceToken = (text: string, from: string, to: string): string =>
  String(text ?? "").replace(TOKEN_PATTERN, (match, name: string) =>
    name.trim().toLowerCase() === from.trim().toLowerCase() ? `{{${to}}}` : match,
  );

/** Removes every `{{token}}` (any spacing or case), and a space it leaves doubled. */
export const removeToken = (text: string, token: string): string =>
  String(text ?? "")
    .replace(/( ?)\{\{([^{}]*)\}\}( ?)/g, (match, before: string, name: string, after: string) =>
      name.trim().toLowerCase() === token.trim().toLowerCase() ? (before && after ? " " : "") : match,
    );

/**
 * `{{name}}` fills in the first name for email (people), and the whole name for a Slack
 * channel label.
 */
export const firstNameFor = (name: string, channel: string): string =>
  channel === "email" || channel === "link" ? name.trim().split(/\s+/)[0] ?? "" : name.trim();

/** `{{last_name}}`: what follows a person's first name; blank for a Slack channel. */
export const lastNameFor = (name: string, channel: string): string =>
  channel === "email" || channel === "link" ? name.trim().split(/\s+/).slice(1).join(" ") : "";
